import { Button, Modal } from "flowbite-react";
import React, { useContext, useEffect, useState } from "react";
import { WorkspaceProject } from "../../../app_client";
import { useNavigate } from "react-router-dom";
import { YMButton, YMInput, YMLoader, YMTable } from "../../../ym-components";
import { HiTrash, HiPlus, HiPencil } from "react-icons/hi";
import { BiSearch } from "react-icons/bi";
import { NewProjectForm } from "./NewProjectForm";
import { formatNumber } from "../../../util";
import { useProjectApi } from "../State";
import ProjectFilter from "./ProjectFilter";
import { TagManager, WorkspaceTag } from "../../../app_client/components";
import { WorkspaceTagType, useWorkspaceApi } from "../../workspace/Api";
import { AuthUserContext } from "../../../AuthUserContext";

const ConfirmDeleteProject: React.FC<{
  projectName: string;
  projectId: string;
  show: boolean;
  onProjectDelete: (project_id: string) => void;
  onModalClose: () => void;
}> = ({ projectName, projectId, show, onModalClose, onProjectDelete }) => {
  return (
    <Modal show={show} onClose={onModalClose}>
      <Modal.Header>
        <div className={"text-xl font-bold text-indigo-900"}>
          Supprimer {projectName}
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <p className="text-base leading-relaxed text-indigo-900">
            Voulez vous vraiment supprimer {projectName} ?
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <YMButton
          text={"Confirmer"}
          onClick={() => onProjectDelete(projectId)}
        />
        <YMButton text={"Annuler"} color={"white"} onClick={onModalClose} />
      </Modal.Footer>
    </Modal>
  );
};

export default function ProjectList(props: any) {
  const projectApi = useProjectApi();
  const workspaceApi = useWorkspaceApi();
  const { workspaceId } = useContext(AuthUserContext) as {
    workspaceId: string;
  };

  const [projects, setProjects] = useState([] as WorkspaceProject[]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [selectedProjectForTags, setSelectedProjectForTags] = useState<any | null>(null);
  const [allTags, setAllTags] = useState<WorkspaceTagType[]>([]);

  const [deleteModalSettings, setDeleteModalSettings] = useState({
    show: false,
    projectId: "",
    projectName: "",
  });

  // Fetch workspace tags
  useEffect(() => {
    if (workspaceApi && workspaceId) {
      workspaceApi.getWorkspaceTags(workspaceId).then(setAllTags);
    }
  }, [workspaceApi, workspaceId]);

  useEffect(() => {
    if (projectApi) {
      projectApi.getProjects(filterName || undefined, filterTagIds.length > 0 ? filterTagIds : undefined).then((projects) => {
        setProjects(projects);
        setIsLoading(false);
      });
    }
  }, [projectApi, filterName, filterTagIds]);

  const deleteProject = async (project_id: string) => {
    if (projectApi) {
      await projectApi.deleteProject(project_id);
      if (isLoading)
        setProjects(projects.filter((p) => p.project_id !== project_id));
      projectApi.getProjects().then(setProjects);
    }
    setDeleteModalSettings((prev) => ({ ...prev, show: false }));
  };

  const displayDeleteModal = (project_id: string) => {
    setDeleteModalSettings({
      show: true,
      projectId: project_id,
      projectName:
        projects.find((p) => p.project_id === project_id)?.name || "",
    });
  };

  const handleOpenTagManager = (project: WorkspaceProject) => {
    setSelectedProjectForTags(project);
    setShowTagManager(true);
  };

  const handleCloseTagManager = () => {
    setShowTagManager(false);
    setSelectedProjectForTags(null);
  };

  const handleCreateTag = async (name: string, color: string): Promise<string> => {
    if (!workspaceApi || !workspaceId) {
      return Promise.reject("Workspace API not available");
    }

    const newTag = await workspaceApi.createWorkspaceTag(workspaceId, name, color);
    setAllTags([...allTags, newTag]);
    return newTag.id || newTag.name;
  };

  const handleSaveTags = async (tagNames: string[]): Promise<void> => {
    if (!selectedProjectForTags) {
      return Promise.reject("No project selected");
    }

    if (!projectApi || !workspaceId) {
      return Promise.reject("Project API not available");
    }

    // Get tag IDs from tag names
    const tagIds = tagNames
      .map((name) => {
        const tag = allTags.find((t) => t.name === name);
        return tag?.id || tag?.name;
      })
      .filter((id): id is string => id !== undefined);

    // Call API to assign tags to project
    await projectApi.setTagsToProject(selectedProjectForTags.project_id, tagIds);

    // Refetch projects to get updated tags
    projectApi.getProjects(filterName || undefined, filterTagIds.length > 0 ? filterTagIds : undefined).then(setProjects);

    return Promise.resolve();
  };

  return (
    <div>
      <NewProjectForm />
      <ConfirmDeleteProject
        {...deleteModalSettings}
        onProjectDelete={deleteProject}
        onModalClose={() =>
          setDeleteModalSettings((prev) => ({ ...prev, show: false }))
        }
      />
      <div>
        <div className="mt-5">
          <ProjectFilter
            onSearch={setFilterName}
            onTagsChange={setFilterTagIds}
            workspaceId={workspaceId}
            availableTags={allTags}
          />
        </div>
        <YMLoader message={"Chargement des projects"} loading={isLoading}>
          {projects.length === 0 && (
            <div className="w-100 mt-10 text-center">
              <div className={"mt-4 text-gray-400"}>
                {" "}
                Vous n'avez pas encore de projets
              </div>
            </div>
          )}

          {projects.length > 0 && (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-lg mt-5">
                <YMTable
                  header={
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Project
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Tags
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Date de création
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Nombre de mots
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Nombre de verbatims
                      </th>
                      <th scope="col" className="px-6 py-3">
                        <span className="sr-only"> x </span>
                      </th>
                    </tr>
                  }
                  rows={projects.map((p) => (
                    <ProjectRow
                      key={p.project_id}
                      project={p}
                      onDelete={() => displayDeleteModal(p.project_id)}
                      onOpenTagManager={() => handleOpenTagManager(p)}
                    />
                  ))}
                />
              </div>
            </>
          )}
        </YMLoader>
      </div>
      {showTagManager && selectedProjectForTags && (
        <TagManager
          show={showTagManager}
          onClose={handleCloseTagManager}
          existingTags={allTags}
          selectedTags={selectedProjectForTags.tags || []}
          onCreateTag={handleCreateTag}
          onSaveTags={handleSaveTags}
        />
      )}
    </div>
  );
}

const ProjectRow: React.FC<{
  project: WorkspaceProject;
  onDelete: () => void;
  onOpenTagManager: () => void;
}> = ({ project, onDelete, onOpenTagManager }) => {
  const navigate = useNavigate();
  return (
    <tr
      key={project.project_id}
      onClick={() =>
          navigate(
            "/project/" + project.project_id + "/graph/?name=" + project.name
          )
      }
      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100"
    >
      <th
        scope="row"
        className="px-6 py-4 font-bold text-indigo-900 capitalize hover:text-indigo-900  cursor-pointer whitespace-nowrap dark:text-white"
      >
        {project.name}
      </th>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2 flex-wrap">
          {project.tags?.map((tag: WorkspaceTagType) => (
            <WorkspaceTag key={tag.name} tag={tag} />
          ))}
          <Button
            size="xs"
            color="indigo"
            onClick={(e) => {
              e.stopPropagation();
              onOpenTagManager();
            }}
            title="Manage tags"
          >
            <HiPencil className="h-3 w-3" />
          </Button>
        </div>
      </td>
      <th
        scope="row"
        className="px-6 py-4 font-normal text-indigo-900 capitalize hover:text-indigo-900  cursor-pointer whitespace-nowrap dark:text-white"
      >
        {formatDate(project.created_at)}
      </th>
      <th
        scope="row"
        className="px-6 py-4 font-normal text-indigo-900 capitalize hover:text-indigo-900  cursor-pointer whitespace-nowrap dark:text-white"
      >
        {formatNumber(project.tokens_count)}
      </th>
            <th
        scope="row"
        className="px-6 py-4 font-normal text-indigo-900 capitalize hover:text-indigo-900  cursor-pointer whitespace-nowrap dark:text-white"
      >
        {formatNumber(project.verbatim_count)}
      </th>
      <td className="px-6 py-4 flex gap-2">
        <Button size="xs" color="gray" onClick={e => {
          e.stopPropagation();
          onDelete()
        }}>
          <HiTrash className="h-5 w-5"/>
        </Button>
      </td>
    </tr>
  );
};

// helpers

const formatDate = (dateString:string) => {
  const options: any = { year: "numeric", month: "long", day: "numeric" }
  return new Date(dateString).toLocaleDateString(undefined, options)
}
